import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiPlay } from "react-icons/hi";

const GALLERY_ITEMS = [
  { type: "image", src: "/assets/PHB Photos/momo.jpeg", alt: "Steamed momos" },
  { type: "image", src: "/assets/PHB Photos/kothey.JPG", alt: "Kothey momos — crispy bottom, soft top" },
  { type: "video", src: "/assets/merged_food_video.webm", alt: "Food preparation montage" },
  { type: "image", src: "/assets/PHB Photos/pan_fried.JPG", alt: "Pan-fried momos with fresh herbs" },
  { type: "image", src: "/assets/PHB Photos/kheema.jpeg", alt: "Keema noodles — Tibetan-style" },
  { type: "image", src: "/assets/PHB Photos/noodles__.JPG", alt: "Hakka noodles tossed in the wok" },
  { type: "video", src: "/assets/outdoor-video.webm", alt: "Outdoor dining ambience" },
  { type: "image", src: "/assets/PHB Photos/momo_.JPG", alt: "Freshly steamed chicken momos" },
];

function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Gallery lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-10"
        aria-label="Close lightbox"
      >
        <HiX className="w-8 h-8" />
      </button>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="max-w-5xl w-full max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === "video" ? (
          <video src={item.src} className="w-full max-h-[85vh] rounded-xl object-contain" controls autoPlay playsInline />
        ) : (
          <img src={item.src} alt={item.alt} className="w-full max-h-[85vh] rounded-xl object-contain" />
        )}
      </motion.div>
    </motion.div>
  );
}

function GalleryCard({ item, index, onClick }) {
  const isLarge = index === 0 || index === 3 || index === 7;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.08 }}
      className={`relative group cursor-pointer overflow-hidden rounded-2xl shadow-md ${
        isLarge ? "md:col-span-2 md:row-span-2" : ""
      }`}
      onClick={() => onClick(item)}
    >
      {item.type === "video" ? (
        <>
          <video
            src={item.src}
            className="w-full h-full min-h-[250px] object-cover transition-transform duration-500 group-hover:scale-105"
            muted loop playsInline autoPlay preload="metadata"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <HiPlay className="w-7 h-7 text-charcoal ml-1" />
            </div>
          </div>
        </>
      ) : (
        <>
          <img src={item.src} alt={item.alt} className="w-full h-full min-h-[250px] object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
        </>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <p className="text-white text-sm font-medium">{item.alt}</p>
      </div>
    </motion.div>
  );
}

export default function Gallery() {
  const [lightboxItem, setLightboxItem] = useState(null);

  const openLightbox = useCallback((item) => {
    setLightboxItem(item);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxItem(null);
    document.body.style.overflow = "";
  }, []);

  return (
    <section id="gallery" className="section-padding bg-beige">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="section-heading">Gallery</h2>
          <div className="accent-divider" />
          <p className="section-subheading">A glimpse into our kitchen, food, and ambience</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 auto-rows-[250px] md:auto-rows-[200px]">
          {GALLERY_ITEMS.map((item, i) => (
            <GalleryCard key={i} item={item} index={i} onClick={openLightbox} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightboxItem && <Lightbox item={lightboxItem} onClose={closeLightbox} />}
      </AnimatePresence>
    </section>
  );
}
